import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { withRouter } from "tools/routerCompat";
import { MCard, MSpinner } from "components/Shared";
import MGridLayout, { MGridLayoutItem } from "components/Shared/MGridLayout";
import CustomerSurveyTypeConfiguration from "components/Customer/Profile/SurveyTypes/CustomerSurveyTypeConfiguration";
import { CustomerDetailsDto, SurveyTypeListDto } from "interfaces/Mueller.Business.Models.ts";
import CustomerSurveyTypeForms from "./CustomerSurveyTypeForms.tsx";
import CustomerSurveyTypeNotifications from "./CustomerSurveyTypeNotifications.tsx";
import { updateHeader, clearHeader } from "actions/layout";
import CustomerSurveyTypeStamps from "components/Customer/SurveyTypes/CustomerSurveyTypeStamps.tsx";
import CustomerExperienceAssignment from "../CustomerExperienceAssignment.tsx";
import PhoenixFormPreview from "components/PhoenixFormsV4/PhoenixFormsDesigner/PhoenixFormPreview.tsx";

interface CustomerSurveyTypeDetailsProps {
  verticalDisplay: boolean;
  // Redux
  selectedSurveyType: SurveyTypeListDto;
  customerDetails: CustomerDetailsDto;
  updateHeader: (headerDefinitions: any) => void;
  clearHeader: () => void;
  // Router
  history: any;
}

export interface PnxFormMetaData {
  formName: string,
  formId: string
}

const  CustomerSurveyTypeDetails = (props: CustomerSurveyTypeDetailsProps) => {
  
  if (!props.selectedSurveyType || !props.customerDetails) {
    return (<MSpinner />);
  }

  const [selectedPnxFormInfo, setSelectedPnxFormInfo] = useState<PnxFormMetaData | null>(null);

  useEffect(() => {
    if (!props.verticalDisplay) {
      props.updateHeader({
        buttons: [{
            name: "Return to Survey Types",
            onClick: () =>  props.history.push({ pathname: "/customer/survey-types" })
          }]
      });
    }
    return () => {
      if (!props.verticalDisplay) {
        props.clearHeader();
      }
    };
  }, []);

  const determinePnxFormPreviewCardChildren = () => {
    
    if (!selectedPnxFormInfo){
      return <p>Please select a form to preview.</p>;
    }

    return (
      selectedPnxFormInfo.formId ?
        <PhoenixFormPreview pnxFormId={selectedPnxFormInfo.formId} />
        :
        <p>Selected form is not a Phoenix form.</p>
    );
  };

  const pnxFormPreviewCardTitle = `Phoenix Form Preview${selectedPnxFormInfo?.formName ? ` - ${selectedPnxFormInfo.formName}` : ''}`;
  const renderPnxFormPreviewInDiv = selectedPnxFormInfo !== null && selectedPnxFormInfo.formId?.length > 0;

  // Define the layout structure using MGridLayout format
  const rows: MGridLayoutItem[][] = [
    // Row 1: CustomerExperienceAssignment (half width in full-screen)
    [
      {
        component: (
          <CustomerExperienceAssignment 
            customerNumber={props.customerDetails.number}
            surveyTypeCode={props.selectedSurveyType.code}
            verticalDisplay={props.verticalDisplay}
          />
        ),
        size: 6,
        verticalSize: 12,
      },
    ],
    // Row 2: Stamps (full width)
    [
      {
        component: <CustomerSurveyTypeStamps verticalDisplay={props.verticalDisplay} />,
        size: 12,
      },
    ],
    // Row 3: Forms (full width)
    [
      {
        component: (
          <CustomerSurveyTypeForms
            verticalDisplay={props.verticalDisplay}
            setSelectedPnxFormInfo={setSelectedPnxFormInfo}
          />
        ),
        size: 12,
      },
    ],
    // Row 4: Phoenix Form Preview (full width)
    [
      {
        component: (
          <MCard
            className="customer-survey-type-pnx-form-preview-card"
            title={pnxFormPreviewCardTitle}
            renderChildrenInDiv={renderPnxFormPreviewInDiv}
          >
            {determinePnxFormPreviewCardChildren()}
          </MCard>
        ),
        size: 12,
      },
    ],
    // Row 5: Notifications (full width)
    [
      {
        component: <CustomerSurveyTypeNotifications verticalDisplay={props.verticalDisplay} />,
        size: 12,
      },
    ],
  ];

  return (
    <MGridLayout
      verticalDisplay={props.verticalDisplay}
      rows={rows}
      padding={props.verticalDisplay ? 0 : 1}
    >
      {/* CustomerSurveyTypeConfiguration returns its own Grid items */}
      <CustomerSurveyTypeConfiguration
        customerNumber={props.customerDetails.number}
        surveyTypeCode={props.selectedSurveyType.code}
        verticalDisplay={props.verticalDisplay}
      />
    </MGridLayout>
  );
};

const mapStateToProps = state => ({
  selectedSurveyType: state.customer.selectedSurveyType,
  customerDetails: state.customer.customerDetails,
});

const mapDispatchToProps = (dispatch) => ({
  updateHeader(headerDefinitions) {
      dispatch(updateHeader(headerDefinitions));
  },
  clearHeader() {
      dispatch(clearHeader());
  },
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(CustomerSurveyTypeDetails));